import axios from "axios";

import { ENDPOINT, API_KEY } from "constants/environment";
import { encryptData, decryptData, getHeaderConfig } from "helpers";
import {
  activePortal,
  portalToken,
  portalRefreshToken,
  accessTokenExpired,
  updatePortalTokens,
  clearPortalSession,
  loadPortalSession,
  markPortalSessionExpired,
} from "helpers/portalSession";

/**
 * Single transport for portal → app-backend calls.
 *
 * ── Always POST ─────────────────────────────────────────────────────────────
 * The portal→middleware hop is POST-only by platform convention, so the verb
 * never varies and the operation is carried by the path
 * (`/tenants/list`, `/roles/update`, …). There is deliberately no get/put/del
 * export here — adding one would reintroduce the inconsistency this replaces.
 *
 * ── Why the response needs two parses ───────────────────────────────────────
 * app-backend's onSend hook encrypts a payload that Fastify has ALREADY
 * serialised, and its encryptData JSON.stringify()s whatever it is given. So the
 * plaintext inside the envelope is a JSON *string*, not an object: decryptData
 * parses it once and yields that string, and the caller must parse again.
 *
 * ── Why unwrapping is not a one-liner ───────────────────────────────────────
 * app-backend answers with FOUR different body shapes, and only the first
 * carries the `{ statusCode, message, data }` envelope:
 *
 *   success            HTTP 200  { statusCode: 200, message, data }
 *   not found          HTTP 200  { statusCode: 404, message }        ← no `data`
 *   business (roles)   HTTP 400  { statusCode: 400, message, data: [] }
 *   business (tenants) HTTP 400  { error: { statusCode, message } }
 *   schema failure     HTTP 400  { error: { code: "FST_ERR_VALIDATION",
 *                                           validation: [ { message } ] } }
 *   server fault       HTTP 500  { error: {} }                       ← no text
 *
 * Note the two business-failure shapes differ by route family: the roles
 * controller returns the standard envelope with an explicit reply.code(400), so
 * it arrives at the TOP level; the tenants controller throws, so setErrorHandler
 * wraps it under `error`. Both were observed against the running backend.
 *
 * The schema-failure case has NO top-level `message` — Error.prototype.message
 * is non-enumerable, so JSON.stringify drops it and the only human-readable text
 * is at error.validation[0].message. Reading `body.message` alone therefore
 * turns every validation error into a blank, and branching on the HTTP status
 * alone turns "Tenant not found" into a success. Both are handled below.
 */

export const GENERIC_FAILURE = "Something went wrong. Please try again.";
const SESSION_EXPIRED = "Your session has expired. Please sign in again.";

/**
 * Sign-out handlers, one per portal, registered by that portal's auth layer.
 *
 * A refresh the SERVER rejected is a dead session, not a failed request. The page
 * has no way to recover it, so returning "your session has expired" for the call
 * site to render left the user reading an error inside a shell they were no longer
 * signed in to — and every subsequent call produced the same error. Signing them
 * out puts them on the login screen, which is the only thing that can be done
 * about it.
 */
const sessionExpiredHandlers = { crm: null, partner: null };

/** Register a portal's sign-out. Returns an unsubscribe. */
export const onSessionExpired = (portal, handler) => {
  sessionExpiredHandlers[portal] = handler;
  return () => {
    if (sessionExpiredHandlers[portal] === handler) sessionExpiredHandlers[portal] = null;
  };
};

/**
 * Token-rotated handlers, one per portal, registered by that portal's auth layer.
 *
 * A refresh replaces BOTH tokens in sessionStorage, which is all this transport
 * needs — every call reads the stored session directly. The React layers do not:
 * they seed from storage once at mount and hold that snapshot, so after a
 * rotation their `accessJWT` is the token the server has just superseded.
 *
 * That copy is not decorative. Both portals hand it to the embedded traveller app
 * as `identity.jwt` (admin/pages/travel/TravelPage.jsx, partner/pages/travel/…),
 * so without this the frame keeps authenticating with an expired token — and the
 * embed has no refresh path of its own to recover with. The portal shell would
 * look fine while Shop Travel quietly stopped working.
 */
const sessionRefreshedHandlers = { crm: null, partner: null };

/** Register a portal's "tokens rotated" handler. Returns an unsubscribe. */
export const onSessionRefreshed = (portal, handler) => {
  sessionRefreshedHandlers[portal] = handler;
  return () => {
    if (sessionRefreshedHandlers[portal] === handler) sessionRefreshedHandlers[portal] = null;
  };
};

/**
 * Sign out of a portal whose session the server has rejected.
 *
 * The stored session is cleared FIRST, and that same clear is what makes this
 * idempotent: a burst of calls all 401 together, and the second one through finds
 * nothing left to expire and returns. Without that, each call in the burst would
 * fire its own sign-out.
 *
 * No logoutAPI call — the refresh family is already dead server-side, which is how
 * we got here, so there is nothing left to revoke.
 *
 * `message` is left for the login screen to show (SMB-4352), so the user learns
 * why they are there instead of meeting a blank sign-in form mid-task.
 */
const expireSession = (portal, message = SESSION_EXPIRED) => {
  if (!portalToken(portal) && !portalRefreshToken(portal)) return;
  clearPortalSession(portal);
  markPortalSessionExpired(portal, message);
  // Absent when a call is made from outside the React tree. Clearing the stored
  // session above is the part that must not be skipped; the handler only drops the
  // in-memory copy so the route guard redirects.
  sessionExpiredHandlers[portal]?.();
};

const ok = (data) => ({ ok: true, data });
const fail = (message, extra) => ({ ok: false, message: message || GENERIC_FAILURE, ...extra });

/**
 * A 403 from hooks/authorization.ts is not always "you lack a grant" — when
 * `code: "ACCOUNT_REVOKED"` rides along, the identity ITSELF has been
 * deactivated or removed since it signed in. Rendering that as an ordinary
 * permission-denied toast left a still-open tab looking "logged in" while
 * every action it tried failed the same way; this signs it out instead, the
 * same as a dead refresh does.
 *
 * ── `httpStatus` ───────────────────────────────────────────────────────────
 * The transport's own status, used ONLY when the body does not carry one of its
 * own — SMB-4173.
 *
 * Most of app-backend answers with an envelope that echoes its status, so
 * `unwrap` finds it there. The authorization hook does not: a refusal is
 * `reply.status(403).send({ message: "You do not have permission..." })`, a body
 * with no `statusCode` at all. Callers were therefore handed a failure carrying
 * a message and `statusCode: undefined`, so a screen could not tell "refused"
 * from "not found" and had to guess from the text.
 *
 * Only ever a FALLBACK: a body that states its own status still wins, which
 * matters because app-backend routinely returns HTTP 200 carrying
 * `statusCode: 400`. Filling in a value that was previously absent cannot flip
 * an existing `statusCode === 409` test from true to false.
 */
const handleErrorResponse = (rawData, portal, httpStatus) => {
  const result = unwrap(rawData);
  if (result.code === "ACCOUNT_REVOKED") {
    expireSession(portal, result.message || SESSION_EXPIRED);
    return fail(result.message, { statusCode: 403, sessionExpired: true, code: result.code });
  }
  if (result.statusCode == null && httpStatus != null) {
    return { ...result, statusCode: httpStatus };
  }
  return result;
};

/** Pull the most specific human-readable message out of any of the shapes above. */
const messageFrom = (body) => {
  if (!body || typeof body !== "object") return "";
  if (body.message) return body.message;
  const e = body.error;
  if (!e || typeof e !== "object") return "";
  if (e.message) return e.message;
  // Schema rejection: the text lives only on the first validation entry, and
  // allErrors is false server-side so there is never more than one.
  const v = Array.isArray(e.validation) ? e.validation[0] : null;
  if (v?.message) {
    const field = v.params?.missingProperty || (v.instancePath || "").replace(/^\//, "");
    return field ? `${field}: ${v.message}` : v.message;
  }
  return "";
};

const unwrap = (raw) => {
  let body;
  try {
    // decryptData parses once; the double-encoding means we parse again. When
    // ENABLE_CRYPTO is off the body is already plain JSON, hence the fallback.
    const once = decryptData(raw);
    body = typeof once === "string" ? JSON.parse(once) : once;
  } catch {
    try {
      body = typeof raw === "string" ? JSON.parse(raw) : raw;
    } catch {
      return fail(GENERIC_FAILURE);
    }
  }

  if (body?.statusCode === 200) return ok(body.data);

  // Anything else is a failure — including HTTP 200 carrying statusCode 404.
  //
  // rawError is preserved additively: the new partner-engine proxy contract
  // (see APImdFiles/FRONTEND_MARKUP_API.md §Errors) wraps the real error at
  // `body.rawError.statusCode`, but the outer envelope's `statusCode` is
  // always 500 on those routes. A caller can inspect `result.rawError` when
  // it needs the real code / details. Consumers that never read it are
  // unaffected.
  return fail(messageFrom(body), {
    statusCode: body?.statusCode ?? body?.error?.statusCode,
    rawError: body?.rawError,
    // Additive, same contract as rawError: some routes (dropnet) return their
    // per-field messages under the envelope's `data` on a 400 —
    // `{ statusCode: 400, message: "Validation failed", data: { validationErrors: [...] } }`.
    // Without this the list is discarded and the operator only sees the
    // generic "Validation failed". Consumers that never read it are unaffected.
    errorData: body?.data,
    // Additive, same contract: the verbatim partner-engine proxies (flight
    // deals — APImdFiles/FRONTEND_FLIGHTDEAL_API.md §6) relay
    // `{ statusCode, error, message, details }` at the real HTTP status, and
    // `details` carries the 400 `validationErrors` and the 409
    // `conflictingId` / `hint`. Consumers that never read it are unaffected.
    details: body?.details,
    // Additive: customer-safe copy the post-booking hotel endpoints send in the
    // FLAT error shape (`body.displayMessage` / `body.details.displayMessage`),
    // meant to be shown verbatim. The wrapped shape keeps it under `rawError`,
    // which is preserved above. `errorReference` is what support traces.
    displayMessage: body?.displayMessage ?? body?.details?.displayMessage,
    errorReference: body?.errorReference ?? body?.rawError?.errorReference,
    // Set only on a 403 from hooks/authorization.ts when the DENIAL means this
    // identity's own account is deactivated/gone, not a missing grant. See its
    // use in postToBackend/postFormToBackend below.
    code: body?.code,
  });
};

/**
 * POST to app-backend and return `{ ok, data }` or `{ ok: false, message }`.
 *
 * The access token is read from the stored portal session rather than passed in,
 * so no call site has to thread it through. `portal` selects which session —
 * the CRM and partner portals keep separate ones — and defaults to whichever
 * portal the page is currently in.
 *
 * That default used to be a flat `"crm"`, which is only right for a call made
 * from the CRM portal. Every module shared by both portals omits the argument,
 * so from `/partnerAdmin/*` they all read the CRM session — which does not
 * exist — and sent an empty `x-access-token`. See `activePortal`.
 */
/**
 * In-flight refresh, per portal — the single-flight latch.
 *
 * This matters more than it looks. Refresh tokens ROTATE: each exchange consumes
 * the presented token. If several requests 401 at once and each fires its own
 * refresh, the second one replays a token the first already consumed — and the
 * server reads a replayed token as theft and revokes the entire family. Parallel
 * refreshes would therefore sign the user out, reliably, under exactly the
 * conditions they are meant to recover from (a burst of calls on page load).
 *
 * So every caller awaits the SAME promise, and only the first performs the
 * exchange.
 */
const inFlightRefresh = { crm: null, partner: null };

/**
 * Exchange the stored refresh token for a fresh pair. At most one at a time.
 *
 * Returns `{ ok, dead }`. `dead` separates a session the server has REJECTED —
 * nothing to be done, sign out — from a refresh that merely could not be
 * delivered. A dropped connection must not sign anyone out: the session may be
 * perfectly good and the next attempt will say so.
 */
const REFRESHED = { ok: true, dead: false };
const REFRESH_REJECTED = { ok: false, dead: true };
const REFRESH_UNREACHABLE = { ok: false, dead: false };

const refreshSession = (portal) => {
  if (inFlightRefresh[portal]) return inFlightRefresh[portal];

  /**
   * No refresh token at all: the access token cannot be renewed, so whatever is
   * stored is unusable.
   *
   * Checked HERE, before the promise below is created, and the latch is
   * deliberately not taken for it. An async function that returns before its
   * first `await` runs to completion SYNCHRONOUSLY — so when this check lived
   * inside the promise, its clean-up fired before the `inFlightRefresh[portal] =`
   * assignment that followed the IIFE, and the assignment then latched an
   * already-settled rejection that nothing would ever clear.
   *
   * The cost of that was the whole mechanism: one refresh attempted while signed
   * out — which is exactly what a burst of 401s after a session ends produces —
   * left every later call returning that cached rejection without touching the
   * network. Signing in again did not help, because the latch outlives the
   * session. The refresh path worked once per page load and then never again.
   */
  if (!portalRefreshToken(portal)) return Promise.resolve(REFRESH_REJECTED);

  const exchange = (async () => {
    const refreshToken = portalRefreshToken(portal);
    if (!refreshToken) return REFRESH_REJECTED;

    try {
      const url = `${ENDPOINT}/app/v1/${portal}/auth/refresh`;
      const response = await axios.post(url, encryptData({ refreshToken }), getHeaderConfig(null, ""));
      const result = unwrap(response?.data);
      if (!result.ok) return REFRESH_REJECTED;

      // Persist the NEW pair immediately. Losing it here would leave the next
      // refresh replaying a consumed token, which revokes the family.
      //
      // ── And the re-read context, which this used to throw away ──────────────
      //
      // The server does not just rotate tokens here: it re-reads the user, their
      // partner and their ROLE from the database on every refresh, deliberately,
      // so that "a role change, suspension or branch move since sign-in must take
      // effect on refresh rather than persist for the life of the family"
      // (PartnerAuthController.refresh). It has always sent `role`,
      // `permissions` and `modules` back with the new pair.
      //
      // Only the three token fields were being stored, so all of that was
      // discarded and the session kept whatever permissions it was minted with
      // until the operator signed out. That was invisible while nothing read
      // permissions. Now that the sidebar and the route guard are driven by
      // them, it is the difference between a revoked grant taking effect within
      // the hour and it not taking effect at all.
      //
      // Guarded with `??` per field rather than spread wholesale: an older
      // deployment that answers with tokens only must leave the cached context
      // in place, not blank it. `savePortalSession` whitelists keys anyway, so
      // an unknown extra field on the response is dropped rather than stored.
      updatePortalTokens(portal, {
        jwtToken: result.data?.jwtToken,
        refreshToken: result.data?.refreshToken,
        accessTokenExpiresAt: result.data?.accessTokenExpiresAt,
        ...(result.data?.user ? { user: result.data.user } : {}),
        ...(result.data?.partner ? { partner: result.data.partner } : {}),
        ...(result.data?.branches ? { branches: result.data.branches } : {}),
        ...(result.data?.role ? { role: result.data.role } : {}),
        ...(result.data?.permissions ? { permissions: result.data.permissions } : {}),
        ...(result.data?.modules ? { modules: result.data.modules } : {}),
        // The tenant's verticals and entitlements are re-read on every refresh
        // too, so a plan change or a newly enabled vertical lands within the
        // hour instead of waiting for the next sign-in.
        ...(result.data?.verticals ? { verticals: result.data.verticals } : {}),
        /**
         * Presence-checked rather than truthy-checked, because `features` has a
         * meaningful falsy value: `null` is the server saying "I could not
         * resolve this tenant's plan". A truthy guard would silently keep the
         * previously cached list in that case and the portal would go on
         * offering features on stale information. An ABSENT key still means an
         * older deployment that cannot answer, and leaves the cache alone.
         */
        ...(result.data && "features" in result.data ? { features: result.data.features } : {}),
      });
      // The stored session is now current but the React layers still hold the
      // token they read at mount, so tell them to re-read it.
      sessionRefreshedHandlers[portal]?.();
      return REFRESHED;
    } catch (error) {
      // A 4xx means the server judged the token; anything else is transport,
      // and a transport failure is not a verdict on the session. That includes
      // a 5xx — a deploy's 502/503 from the load balancer never reached the
      // refresh handler at all — so it is retried, not treated as a sign-out.
      const status = error?.response?.status;
      return status >= 400 && status < 500 ? REFRESH_REJECTED : REFRESH_UNREACHABLE;
    }
  })();

  /**
   * Released from a `.finally()` on the promise rather than from inside it.
   * A `.finally()` callback is always a microtask, never synchronous, so the
   * assignment below is guaranteed to happen first — which is the ordering the
   * previous version could not guarantee.
   *
   * The identity check matters for the same reason: only the run that TOOK the
   * latch may release it, so a later refresh that has already claimed it is not
   * cleared out from under itself by its predecessor finishing.
   */
  const run = exchange.finally(() => {
    if (inFlightRefresh[portal] === run) inFlightRefresh[portal] = null;
  });

  inFlightRefresh[portal] = run;
  return run;
};

/**
 * Keep a portal session alive on a TIMER, not only when the portal makes a call.
 *
 * ── The hole this closes ────────────────────────────────────────────────────
 * Every other refresh in this module is LAZY: `postToBackend` renews the access
 * token when it is about to spend one (see the `accessTokenExpired` check
 * there). That is sufficient for ordinary screens, which cannot be used without
 * issuing requests — but it is not sufficient for the embedded traveller app.
 *
 * On `/{admin,partnerAdmin}/travel/*` the operator works entirely INSIDE a
 * cross-origin iframe. Those requests are the traveller app's own, sent straight
 * to app-backend carrying the portal token this module handed over; they never
 * pass through `postToBackend`, so they never trigger the lazy refresh. The
 * portal itself, meanwhile, issues nothing at all while that screen is open. So
 * the access token simply lapsed — and the frame cannot renew it either, because
 * app-frontend's `scheduleEmbedRefresh` only renews a token carrying
 * `scope: "embed"`, which a first-party portal token does not have.
 *
 * The result was an embedded session that died `PORTAL_ACCESS_EXPIRY_SECONDS`
 * after sign-in with nothing to recover it: every call from the frame answered
 * `FAST_JWT_EXPIRED`. Locally that is one minute (the .env sets 60 precisely to
 * exercise this path); deployed it is 24 hours, which is the same bug wearing a
 * disguise — a console left open overnight.
 *
 * ── Why it self-reschedules instead of listening ────────────────────────────
 * `sessionRefreshedHandlers` is ONE SLOT per portal (see `onSessionRefreshed`),
 * already held by that portal's auth layer. Registering here would silently
 * replace it, so this loop re-reads the stored expiry on every tick instead —
 * which also makes it self-correcting when the lazy path rotated the token in
 * between: it simply finds a later expiry and waits again rather than rotating a
 * perfectly good token.
 *
 * An ALREADY-expired (or unknown) expiry refreshes on the first tick, so
 * mounting this also repairs a session that had already lapsed.
 *
 * Returns an unsubscribe. Safe to call when signed out — it stops on the first
 * tick that finds no refresh token.
 */
const KEEPALIVE_SKEW_MS = 15_000;
const KEEPALIVE_MIN_MS = 5_000;
const KEEPALIVE_RETRY_MS = 10_000;

export const keepSessionFresh = (portal) => {
  let timer = null;
  let stopped = false;

  /** ms until this token should be rotated, or null when there is no expiry to read. */
  const msUntilRefresh = () => {
    const iso = loadPortalSession(portal)?.accessTokenExpiresAt;
    if (!iso) return null;
    const at = Date.parse(iso);
    if (!Number.isFinite(at)) return null;
    // Never returns less than the floor, so the boundary test below is exact.
    return Math.max(at - Date.now() - KEEPALIVE_SKEW_MS, KEEPALIVE_MIN_MS);
  };

  const schedule = (ms) => {
    clearTimeout(timer);
    timer = setTimeout(tick, ms);
  };

  async function tick() {
    if (stopped) return;
    // Signed out, or a session with no refresh token: nothing to keep alive, and
    // refreshSession would only return REFRESH_REJECTED forever.
    if (!portalRefreshToken(portal)) return;

    const wait = msUntilRefresh();
    // Still comfortably valid — the lazy path must have rotated it since the last
    // tick. Wait again rather than burn a rotation.
    if (wait !== null && wait > KEEPALIVE_MIN_MS) {
      schedule(wait);
      return;
    }

    const result = await refreshSession(portal);
    if (stopped) return;
    // The server rejected the session. Sign out HERE and do not retry —
    // hammering a revoked family is how a replay looks from the server.
    //
    // This used to just return, leaving the sign-out to "the 401 path" in
    // postToBackend. But the screens this loop exists for are exactly the ones
    // where that path never runs: on /travel/* the portal issues no calls, and
    // the frame's calls go straight to app-backend. So the access token lapsed
    // ~15s later, the frame's requests failed without a word, and the operator
    // sat on a dead Stays page until a hard reload found the session gone
    // (SMB-4352).
    if (result?.dead) {
      expireSession(portal);
      return;
    }
    schedule(result?.ok ? msUntilRefresh() ?? KEEPALIVE_RETRY_MS : KEEPALIVE_RETRY_MS);
  }

  const first = msUntilRefresh();
  schedule(first === null ? KEEPALIVE_MIN_MS : first);

  return () => {
    stopped = true;
    clearTimeout(timer);
  };
};

/**
 * Renew the session because the EMBEDDED FRAME asked — the portal's answer to
 * `zenvoya:session-recover` (TravelAppFrame.jsx), which the traveller app now
 * sends with `reason: "token-expired"` when it drops one of its own calls for
 * holding a lapsed token.
 *
 * ── Why the frame has to be able to ask ────────────────────────────────────
 * `keepSessionFresh` above is the primary renewal on /travel/*, but it is a
 * timer, and a timer's delay is not wall-clock time: a backgrounded tab is
 * throttled and a sleeping machine does not count down at all, so the loop can
 * wake up AFTER the token it was guarding has expired. In that window the
 * frame's fetchApi drops every call before it reaches the network (it refuses
 * to send an expired token), and the hotel search treats the `null` as "no
 * results" — Search does nothing, with no request and no error. The frame
 * cannot renew a portal token itself, so it asks, and this is what answers.
 *
 * ── Outcomes ───────────────────────────────────────────────────────────────
 *   VALID        the stored token is still good; nothing renewed. Re-post it.
 *   REFRESHED    rotated. `onSessionRefreshed` has ALREADY fired, so the React
 *                layer is re-reading the new token, and it reaches the frame
 *                through the identity prop — not through the caller.
 *   EXPIRED      the server refused the refresh: signed out HERE, with the
 *                "session expired" notice left for the login screen, exactly as
 *                `keepSessionFresh` does on a refusal.
 *   UNREACHABLE  5xx or no response. Not a verdict on the session, so nothing
 *                is changed; the frame keeps its notice, which offers Retry.
 *   SIGNED_OUT   no session stored at all. Nothing to renew, and nothing to
 *                re-post: whatever token the caller still renders is one a
 *                sign-out is already removing.
 *
 * ── Never a second refresh path ────────────────────────────────────────────
 * Goes through `refreshSession` and nothing else. Refresh tokens rotate, so an
 * exchange of its own racing `keepSessionFresh`'s or `postToBackend`'s would be
 * a replay — the server revokes the whole family and signs the operator out.
 * Through the latch, a frame asking while the timer is mid-refresh simply
 * awaits that same exchange.
 *
 * Only renews a token that has actually EXPIRED (the same `accessTokenExpired`
 * test the proactive path uses), so a frame that asks repeatedly cannot burn
 * rotations: once renewed, every later ask answers VALID without a request.
 */
export const SESSION_RENEWAL = Object.freeze({
  VALID: "valid",
  REFRESHED: "refreshed",
  EXPIRED: "expired",
  UNREACHABLE: "unreachable",
  SIGNED_OUT: "signed-out",
});

export const renewSessionForFrame = async (portal = activePortal()) => {
  if (!portalToken(portal)) return { status: SESSION_RENEWAL.SIGNED_OUT, jwt: "" };
  if (!accessTokenExpired(portal)) return { status: SESSION_RENEWAL.VALID, jwt: portalToken(portal) };

  const result = await refreshSession(portal);
  // Re-read rather than taken from the exchange: the stored session is what
  // every other reader sees, and this is the token the React layer is about to
  // hand the frame.
  if (result.ok) return { status: SESSION_RENEWAL.REFRESHED, jwt: portalToken(portal) };
  if (result.dead) {
    expireSession(portal);
    return { status: SESSION_RENEWAL.EXPIRED, jwt: "" };
  }
  return { status: SESSION_RENEWAL.UNREACHABLE, jwt: "" };
};

const doPost = (path, body, portal) =>
  axios.post(`${ENDPOINT}${path}`, encryptData(body), getHeaderConfig(null, portalToken(portal)));

/**
 * Multipart variant of doPost, for endpoints that carry a binary file.
 *
 * Two deliberate differences from doPost:
 *
 *   • NO encryptData. A File cannot survive JSON.stringify — it serialises to `{}`
 *     — so the body has to travel as real multipart.
 *   • NO Content-Type header. getHeaderConfig pins `text/plain` for the ciphertext
 *     string, but multipart needs a generated boundary, and only axios/the browser
 *     can supply one. Setting it by hand produces a body the server cannot parse.
 *
 * The auth headers still go, so the session works exactly as it does elsewhere.
 */
const doPostForm = (path, formData, portal) =>
  axios.post(`${ENDPOINT}${path}`, formData, {
    headers: {
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
      "x-access-token": portalToken(portal),
      "x-api-key": API_KEY,
    },
  });

export const postToBackend = async (path, body = {}, portal = activePortal()) => {
  // Proactive: if the access token has already expired, refresh before spending a
  // round trip on a call that would 401. The refresh token outlives it.
  if (accessTokenExpired(portal) && !path.includes("/auth/")) {
    const refresh = await refreshSession(portal);
    // Rejected here means the request below is certain to 401, so sign out now
    // rather than spend a round trip proving it.
    if (refresh.dead) {
      expireSession(portal);
      return fail(SESSION_EXPIRED, { statusCode: 401, sessionExpired: true });
    }
  }

  try {
    return unwrap((await doPost(path, body, portal))?.data);
  } catch (error) {
    // Reactive: a 401 means the access token lapsed between the check and the
    // call, or was rejected. Refresh once and retry exactly once — retrying a
    // second time would loop on a genuinely dead session.
    if (error?.response?.status === 401 && !path.includes("/auth/")) {
      const refresh = await refreshSession(portal);
      if (refresh.ok) {
        try {
          return unwrap((await doPost(path, body, portal))?.data);
        } catch (retryError) {
          if (retryError?.response?.data !== undefined) return handleErrorResponse(retryError.response.data, portal);
          // A 401 on the retry, holding a token the server just issued, is the
          // session being refused rather than merely stale.
          if (retryError?.response?.status === 401) expireSession(portal);
          return fail(SESSION_EXPIRED, { statusCode: 401, sessionExpired: true });
        }
      }
      if (refresh.dead) expireSession(portal);
      return fail(SESSION_EXPIRED, { statusCode: 401, sessionExpired: true });
    }

    // Business and schema failures arrive as real HTTP 400s, so they land here
    // rather than in the success path — their body still holds the message.
    // A 403 is here too now: most are an ordinary missing grant, but
    // handleErrorResponse signs the caller out when the body says their
    // account itself was deactivated rather than showing that as a toast.
    if (error?.response?.data !== undefined) return handleErrorResponse(error.response.data, portal, error?.response?.status);
    return fail(GENERIC_FAILURE);
  }
};

/**
 * POST a FormData body — for the endpoints that carry a binary file, currently
 * only Upload Brand Asset (§17.3).
 *
 * ── Why this lives here and not in its own file ─────────────────────────────
 * Not for the axios call, which is trivial, but for the REFRESH LATCH above.
 * Refresh tokens rotate, and a second concurrent refresh replays a token the
 * first already consumed — which the server reads as theft and revokes the whole
 * family. A separate transport would need its own latch, and two latches are no
 * latch at all. Sharing `refreshSession` is the whole point.
 *
 * The docstring at the top of this file says there is deliberately no
 * get/put/del export. That is about HTTP VERBS — the portal hop is POST-only and
 * still is. This is a POST with a different body encoding, not a new verb.
 *
 * The response still comes back encrypted, so it goes through the same `unwrap`.
 */
export const postFormToBackend = async (path, formData, portal = activePortal()) => {
  if (accessTokenExpired(portal)) {
    const refresh = await refreshSession(portal);
    if (refresh.dead) {
      expireSession(portal);
      return fail(SESSION_EXPIRED, { statusCode: 401, sessionExpired: true });
    }
  }

  try {
    return unwrap((await doPostForm(path, formData, portal))?.data);
  } catch (error) {
    if (error?.response?.status === 401) {
      const refresh = await refreshSession(portal);
      if (refresh.ok) {
        try {
          return unwrap((await doPostForm(path, formData, portal))?.data);
        } catch (retryError) {
          if (retryError?.response?.data !== undefined) return handleErrorResponse(retryError.response.data, portal);
          if (retryError?.response?.status === 401) expireSession(portal);
          return fail(SESSION_EXPIRED, { statusCode: 401, sessionExpired: true });
        }
      }
      if (refresh.dead) expireSession(portal);
      return fail(SESSION_EXPIRED, { statusCode: 401, sessionExpired: true });
    }

    if (error?.response?.data !== undefined) return handleErrorResponse(error.response.data, portal, error?.response?.status);
    return fail(GENERIC_FAILURE);
  }
};
